import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPartner,
  listPartners,
  PartnerApiError,
  type PartnerApiRow,
  type PartnerListQuery,
  type PartnerListResponse,
} from '@/api/partners';

const DEFAULTS = {
  PAGE: 1,
  PAGE_SIZE: 10,
  SORT_BY: 'partner_name',
  ORDER: 'asc' as const,
} as const;

interface UsePartnersReturn {
  partners: PartnerApiRow[];
  pagination: PartnerListResponse['pagination'] | undefined;
  isLoading: boolean;
  error: string | null;
  filters: PartnerListQuery;
  setSearch: (v: string) => void;
  setStatus: (v: PartnerListQuery['status']) => void;
  setPage: (v: number) => void;
  setSort: (sortBy: string, order: 'asc' | 'desc') => void;
  refetch: () => void;
}

export const usePartners = (): UsePartnersReturn => {
  const [data, setData] = useState<PartnerListResponse | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<PartnerListQuery>({
    page: DEFAULTS.PAGE,
    pageSize: DEFAULTS.PAGE_SIZE,
    sortBy: DEFAULTS.SORT_BY,
    order: DEFAULTS.ORDER,
  });

  // Guards against out-of-order responses: only the newest request applies its
  // result. Without this, a slow earlier request can overwrite a later one and
  // render rows that don't match the current filters.
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const result = await listPartners(filters);
      if (requestId !== requestIdRef.current) return;
      setData(result.data);
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load partners');
      setData(undefined);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: DEFAULTS.PAGE }));
  }, []);

  const setStatus = useCallback((status: PartnerListQuery['status']) => {
    setFilters((prev) => {
      if (status === undefined) {
        const { status: _removed, ...rest } = prev;
        return { ...rest, page: DEFAULTS.PAGE };
      }
      return { ...prev, status, page: DEFAULTS.PAGE };
    });
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setSort = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sortBy, order, page: DEFAULTS.PAGE }));
  }, []);

  const partners = useMemo(() => data?.data ?? [], [data]);
  const pagination = useMemo(() => data?.pagination, [data]);

  return {
    partners,
    pagination,
    isLoading,
    error,
    filters,
    setSearch,
    setStatus,
    setPage,
    setSort,
    refetch: fetchData,
  };
};

interface UsePartnerReturn {
  partner: PartnerApiRow | undefined;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => void;
}

export const usePartner = (partyId: string): UsePartnerReturn => {
  const [partner, setPartner] = useState<PartnerApiRow | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchData = useCallback(async () => {
    if (!partyId) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      const result = await getPartner(partyId);
      setPartner(result.data);
    } catch (e: unknown) {
      if (e instanceof PartnerApiError && e.status === 404) {
        setNotFound(true);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load partner');
      }
    } finally {
      setIsLoading(false);
    }
  }, [partyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { partner, isLoading, error, notFound, refetch: fetchData };
};
